import React, { FunctionComponent, useEffect, useState } from "react";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import Paragraph from "components/Paragraph/Paragraph";
import combineClasses from "helpers/combine-classes";
import getElementId from "helpers/getElementId";
import styles from "./SkaalDigitalPour.module.scss";
import SkaalDigitalPourProps from "./SkaalDigitalPourProps.interface";
import useSkaalStyles from "./helpers/useSkaalStyles";
import BreweryLogoPlaceholder from "assets/brewery-placeholder.jpg";
import IconViewer from "components/IconViewer/IconViewer";
import Dictionary from "components/Dictionary/Dictionary";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useAnchor from "blocks/hooks/useAnchor";

const SkaalDigitalPour: FunctionComponent<SkaalDigitalPourProps> = (props) => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const skaalStyles = useSkaalStyles(props.settings);

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const getPercentage = (percentage: number): string => {
        if (percentage < 15) {
            return "0";
        }
        if (percentage < 26) {
            return "25";
        }
        else if (percentage < 58) {
            return "50";
        }
        else if (percentage < 80) {
            return "75";
        }
        else {
            return "100";
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const result = await fetch(
                    props.settings.apiLink
                );
                const json = await result.json();
                setData(json);
                setIsLoading(false);
            }
            catch (error: any) {
                setError(error);
                setIsLoading(false);
            }
        };
        if (props.settings.apiLink) fetchData();
    }, []);

    return (
        <section className={combineClasses(styles.wrapper, ...wrapperClasses)} style={{ ...wrapperStyles, ...skaalStyles, ...splitStyles }} id={identifier}>
            <div className={combineClasses(styles.container, ...splitClasses, ...wrapperClasses)}>
                <div className={styles.content}>
                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading key={`head_${props.contentUdi}`} className={styles.headline} text={props.content.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight} />
                    ) : null}

                    {/* Text */}
                    {props.content.bodyText ? (
                        <Paragraph key={`para_${props.contentUdi}`} className={styles.text} innerHTML={props.content.bodyText} />
                    ) : null}
                </div>

                {/* Beverages */}
                {isLoading ? (
                    <div className={styles.loading}>
                        <div className={styles.loadingText}>Loading...</div>
                    </div>
                ) : error ? (
                    <div className={styles.error}>
                        <div className={styles.errorText}>{error}</div>
                    </div>
                ) : (
                    <div className={styles.beverages}>
                        {data && data.length > 0 ? data.map((beverage: any) => {
                            const percentage = getPercentage(Math.floor(beverage.MenuItemProductDetail.PercentFull * 100));
                            return (
                                <div className={styles.beverage} key={beverage.Id}>
                                    <div className={styles.details_left}>
                                        <div className={styles.beverage_numbrer}>
                                            {beverage.MenuItemDisplayDetail.DisplayName}
                                        </div>
                                        <div className={styles.brewery_image}>
                                            {beverage.MenuItemProductDetail?.Beverage?.Brewery?.LogoImageUrl ? (
                                                <img className={styles.image} src={beverage.MenuItemProductDetail.Beverage.Brewery.LogoImageUrl} alt="Brewery Logo" />
                                            ) : (
                                                <img className={styles.image} src={BreweryLogoPlaceholder} alt="Brewery Logo" />
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles.details_right}>
                                        <div className={styles.text_top}>
                                            <h2 className={styles.brewery_name}>
                                                {beverage.MenuItemProductDetail.Beverage?.Brewery?.BreweryName ? beverage.MenuItemProductDetail.Beverage.Brewery.BreweryName : ""}
                                            </h2>
                                            <span className={styles.beverage_type}>
                                                {beverage.MenuItemProductDetail.Beverage.BeverageName ? beverage.MenuItemProductDetail.Beverage.BeverageName : ""}
                                            </span>
                                        </div>
                                        <div className={styles.beverage_details}>
                                            <span className={styles.style}><Dictionary name="DIGITAL_POUR_STYLE" />: </span> <span>{beverage.MenuItemProductDetail.Beverage?.BeerStyle?.StyleName ? beverage.MenuItemProductDetail.Beverage.BeerStyle.StyleName : ""}</span> <span className={styles.separator}>|</span> <span>ABV {beverage.MenuItemProductDetail.Beverage.Abv ? beverage.MenuItemProductDetail.Beverage.Abv : ""}%</span>
                                        </div>
                                        <div className={styles.beverage_prices}>
                                            {beverage.MenuItemProductDetail.Prices && beverage.MenuItemProductDetail.Prices.length > 0 ? beverage.MenuItemProductDetail.Prices.map((price: any) => (
                                                <div key={price.Id}>
                                                    <span className={combineClasses(styles.price, styles.highlight)}>
                                                        {price.Price ? price.Price : ""}
                                                    </span>
                                                    <span>
                                                        <Dictionary name="DIGITAL_POUR_CURRENCY" />/{price.DisplayName ? price.DisplayName : ""}
                                                    </span>
                                                </div>
                                            )) : null}
                                        </div>
                                    </div>
                                    <div className={styles.keg_percentage}>
                                        <IconViewer name={`keg-${percentage}`} />
                                        <Dictionary name="DIGITAL_POUR_KEG" />: {beverage.MenuItemProductDetail.PercentFull ? Math.floor(beverage.MenuItemProductDetail.PercentFull * 100) : "N/A"}%
                                    </div>
                                </div>
                            );
                        }) : null}
                    </div>
                )}
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={props.contentUdi} {...props.settings.backgroundMedia[0]} />
            ) : null}
        </section>
    );
};

export default SkaalDigitalPour;